import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { 
  Building, 
  Home, 
  Users, 
  MapPin, 
  Phone, 
  ArrowRight,
  Check,
  Shield,
  Server
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface SimpleOrganizationSetupProps {
  userData: any;
  onComplete: (setupData: any) => void;
}

const GlassPanel: React.FC<{ children: React.ReactNode; className?: string; border?: string }> = ({ children, className = '', border = 'border-white/10' }) => (
    <div className={`bg-white/5 backdrop-blur-2xl border ${border} rounded-3xl p-6 md:p-8 shadow-[0_0_30px_rgba(0,0,0,0.5)] ${className}`}>
        {children}
    </div>
);

export function SimpleOrganizationSetup({ userData, onComplete }: SimpleOrganizationSetupProps) {
  const [selectedType, setSelectedType] = useState<'organization' | 'household' | null>(null);
  const [orgData, setOrgData] = useState({
    name: '',
    type: '',
    address: '',
    phone: '',
    website: '',
    employeeCount: ''
  });
  const [householdData, setHouseholdData] = useState({
    houseName: '',
    address: '',
    members: '',
    propertyType: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleComplete = async () => {
    setIsLoading(true);
    const setupData = {
      organization_name: selectedType === 'organization' ? orgData.name : householdData.houseName,
      organization_type: selectedType,
      address: selectedType === 'organization' ? orgData.address : householdData.address,
      phone: orgData.phone || userData?.phone || '',
      ...(selectedType === 'organization' ? {
        website: orgData.website,
        employee_count: orgData.employeeCount
      } : {
        members: householdData.members,
        property_type: householdData.propertyType
      })
    };
    
    try {
      const token = localStorage.getItem('csense_auth_token');
      const response = await fetch('/api/user/complete-onboarding', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(setupData)
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('Hub parameters established:', result);
      } else {
        console.error('Failed to anchor hub:', response.statusText);
      }
    } catch (error) {
      console.error('Error during hub anchoring:', error);
    } finally {
        setTimeout(() => {
            onComplete(setupData);
            setIsLoading(false);
        }, 800);
    }
  };

  const isFormValid = () => {
    if (!selectedType) return false;
    
    if (selectedType === 'organization') {
      return orgData.name && orgData.address;
    } else {
      return householdData.houseName && householdData.address;
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0b14] flex flex-col items-center justify-center p-4 relative overflow-hidden text-white">
      {/* Immersive Neural Background */}
      <div className="absolute inset-0 z-0 pointer-events-none">
         <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/10 blur-[120px] rounded-full mix-blend-screen" />
         <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-600/10 blur-[120px] rounded-full mix-blend-screen" />
         <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03] mix-blend-overlay" />
         {/* Grid lines */}
         <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_80%)]" />
      </div>

      <div className="w-full max-w-3xl relative z-10 space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700">
        
        {/* Header Unit */}
        <div className="text-center space-y-4 mb-4">
           <div className="inline-flex items-center justify-center p-4 rounded-3xl bg-blue-500/10 border border-blue-500/20 mb-2 shadow-[0_0_30px_rgba(59,130,246,0.2)]">
               <Server className="w-8 h-8 text-blue-400" />
           </div>
           <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white to-blue-200">
              Establish Network Anchor
           </h1>
           <p className="text-[10px] md:text-xs font-bold text-blue-400 tracking-[0.3em] uppercase max-w-lg mx-auto">
              Define the physical operational domain for C.S.E.N.S.E nodes
           </p>
        </div>

        <GlassPanel border="border-white/10">
          <div className="mb-6 flex items-center justify-between border-b border-white/10 pb-4">
             <h2 className="text-xs font-black uppercase tracking-widest text-blue-100/60 drop-shadow-md">Select Deployment Profile</h2>
             <span className="text-[10px] font-mono text-blue-400 bg-blue-500/10 px-2 py-1 rounded">PHASE 1/2</span>
          </div>
          
          <div className="space-y-8">
            {/* Selection Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div 
                className={`relative cursor-pointer transition-all duration-300 rounded-3xl p-6 border-2 backdrop-blur-xl group overflow-hidden ${
                  selectedType === 'organization' 
                    ? 'border-blue-500 bg-blue-600/10 shadow-[0_0_30px_rgba(59,130,246,0.15)] z-10' 
                    : 'border-white/5 bg-black/40 hover:border-white/20 hover:bg-white/5'
                }`}
                onClick={() => setSelectedType('organization')}
              >
                {selectedType === 'organization' && (
                   <motion.div layoutId="selection-glow" className="absolute inset-0 bg-blue-500/5 blur-2xl z-0" />
                )}
                <div className="relative z-10 text-center flex flex-col h-full">
                  <div className="flex items-center justify-center mb-6 relative">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-colors ${
                      selectedType === 'organization' 
                        ? 'bg-blue-500/20 border border-blue-500/30' 
                        : 'bg-white/5 border border-white/10 group-hover:bg-white/10'
                    }`}>
                      <Building className={`w-8 h-8 ${
                        selectedType === 'organization' ? 'text-blue-400' : 'text-blue-100/40'
                      }`} />
                    </div>
                    {selectedType === 'organization' && (
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute -top-2 -right-2 bg-blue-500 rounded-full p-1.5 shadow-[0_0_10px_rgba(59,130,246,0.8)] border border-blue-400">
                        <Check className="h-4 w-4 text-white" strokeWidth={3} />
                      </motion.div>
                    )}
                  </div>
                  <h3 className={`text-xl font-black uppercase tracking-tight mb-2 ${selectedType === 'organization' ? 'text-white' : 'text-blue-100/80'}`}>Enterprise Grid</h3>
                  <p className="text-xs text-blue-100/50 font-light mb-6 flex-1">
                    Commercial matrices, infinite scaling, and multi-tenant sub-nodes.
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 mt-auto">
                    <Badge variant="outline" className={`text-[9px] uppercase tracking-widest bg-transparent border-white/10 ${selectedType === 'organization' ? 'text-blue-300' : 'text-blue-100/40'}`}>Global Sync</Badge>
                    <Badge variant="outline" className={`text-[9px] uppercase tracking-widest bg-transparent border-white/10 ${selectedType === 'organization' ? 'text-blue-300' : 'text-blue-100/40'}`}>O-Auth Access</Badge>
                  </div>
                </div>
              </div>

              <div 
                className={`relative cursor-pointer transition-all duration-300 rounded-3xl p-6 border-2 backdrop-blur-xl group overflow-hidden ${
                  selectedType === 'household' 
                    ? 'border-emerald-500 bg-emerald-600/10 shadow-[0_0_30px_rgba(16,185,129,0.15)] z-10' 
                    : 'border-white/5 bg-black/40 hover:border-white/20 hover:bg-white/5'
                }`}
                onClick={() => setSelectedType('household')}
              >
                {selectedType === 'household' && (
                   <motion.div layoutId="selection-glow" className="absolute inset-0 bg-emerald-500/5 blur-2xl z-0" />
                )}
                <div className="relative z-10 text-center flex flex-col h-full">
                  <div className="flex items-center justify-center mb-6 relative">
                    <div className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-colors ${
                      selectedType === 'household' 
                        ? 'bg-emerald-500/20 border border-emerald-500/30' 
                        : 'bg-white/5 border border-white/10 group-hover:bg-white/10'
                    }`}>
                      <Home className={`w-8 h-8 ${
                        selectedType === 'household' ? 'text-emerald-400' : 'text-emerald-100/40'
                      }`} />
                    </div>
                    {selectedType === 'household' && (
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute -top-2 -right-2 bg-emerald-500 rounded-full p-1.5 shadow-[0_0_10px_rgba(16,185,129,0.8)] border border-emerald-400">
                        <Check className="h-4 w-4 text-white" strokeWidth={3} />
                      </motion.div>
                    )}
                  </div>
                  <h3 className={`text-xl font-black uppercase tracking-tight mb-2 ${selectedType === 'household' ? 'text-white' : 'text-blue-100/80'}`}>Forward Base</h3>
                  <p className="text-xs text-emerald-100/50 font-light mb-6 flex-1">
                    Localized residential deployment, simple rapid-provisioning protocol.
                  </p>
                  <div className="flex flex-wrap justify-center gap-2 mt-auto">
                    <Badge variant="outline" className={`text-[9px] uppercase tracking-widest bg-transparent border-white/10 ${selectedType === 'household' ? 'text-emerald-300' : 'text-blue-100/40'}`}>Local Edge</Badge>
                    <Badge variant="outline" className={`text-[9px] uppercase tracking-widest bg-transparent border-white/10 ${selectedType === 'household' ? 'text-emerald-300' : 'text-blue-100/40'}`}>Mobile Command</Badge>
                  </div>
                </div>
              </div>
            </div>

            {/* Dynamic Form Area */}
            <AnimatePresence mode="wait">
              {selectedType && (
                 <motion.div
                    key={selectedType}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                 >
                    <div className={`p-8 rounded-3xl bg-black/40 border border-white/5 relative mt-4 ${selectedType === 'organization' ? 'border-t-2 border-t-blue-500/50' : 'border-t-2 border-t-emerald-500/50'}`}>
                       <h3 className="text-sm font-black uppercase tracking-widest mb-6 flex items-center gap-3">
                          {selectedType === 'organization' ? <Building className="w-5 h-5 text-blue-400" /> : <Shield className="w-5 h-5 text-emerald-400" />}
                          {selectedType === 'organization' ? 'Enterprise Core Parameters' : 'Base Tactical Coordinates'}
                       </h3>

                       {selectedType === 'organization' ? (
                          <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-blue-100/60">Matrix Designation *</label>
                                <input
                                  type="text"
                                  placeholder="e.g., Cyberdyne Systems"
                                  value={orgData.name}
                                  onChange={(e) => setOrgData({...orgData, name: e.target.value})}
                                  className="w-full bg-[#0a0b14] border border-white/10 text-white placeholder:text-white/20 py-4 px-4 rounded-xl focus:outline-none focus:border-blue-500/50 transition-colors"
                                />
                              </div>
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-blue-100/60">Sector Classification</label>
                                <input
                                  type="text"
                                  placeholder="e.g., Aerospace, Defense"
                                  value={orgData.type}
                                  onChange={(e) => setOrgData({...orgData, type: e.target.value})}
                                  className="w-full bg-[#0a0b14] border border-white/10 text-white placeholder:text-white/20 py-4 px-4 rounded-xl focus:outline-none focus:border-blue-500/50 transition-colors"
                                />
                              </div>
                            </div>
                            
                            <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-blue-100/60">Geographical Coordinates *</label>
                              <div className="relative group">
                                <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-blue-100/40 group-focus-within:text-blue-400 transition-colors" />
                                <input
                                  type="text"
                                  placeholder="Physical grid address..."
                                  value={orgData.address}
                                  onChange={(e) => setOrgData({...orgData, address: e.target.value})}
                                  className="w-full bg-[#0a0b14] border border-white/10 text-white placeholder:text-white/20 py-4 pl-12 pr-4 rounded-xl focus:outline-none focus:border-blue-500/50 transition-colors"
                                />
                              </div>
                            </div>

                            <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-blue-100/60">Command Line (Voice fallback)</label>
                              <div className="relative group">
                                <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-blue-100/40 group-focus-within:text-blue-400 transition-colors" />
                                <input
                                  type="text"
                                  placeholder="Secure landline or mobile auth..."
                                  value={orgData.phone}
                                  onChange={(e) => setOrgData({...orgData, phone: e.target.value})}
                                  className="w-full bg-[#0a0b14] border border-white/10 text-white placeholder:text-white/20 py-4 pl-12 pr-4 rounded-xl focus:outline-none focus:border-blue-500/50 transition-colors font-mono"
                                />
                              </div>
                            </div>
                          </div>
                       ) : (
                          <div className="space-y-6">
                            <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-emerald-100/60">Base Identifier *</label>
                              <input
                                type="text"
                                placeholder="e.g., Sector 7 Alpha"
                                value={householdData.houseName}
                                onChange={(e) => setHouseholdData({...householdData, houseName: e.target.value})}
                                className="w-full bg-[#0a0b14] border border-white/10 text-white placeholder:text-white/20 py-4 px-4 rounded-xl focus:outline-none focus:border-emerald-500/50 transition-colors"
                              />
                            </div>
                            
                            <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-emerald-100/60">Physical Address *</label>
                              <div className="relative group">
                                <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-emerald-100/40 group-focus-within:text-emerald-400 transition-colors" />
                                <input
                                  type="text"
                                  placeholder="Structure physical location..."
                                  value={householdData.address}
                                  onChange={(e) => setHouseholdData({...householdData, address: e.target.value})}
                                  className="w-full bg-[#0a0b14] border border-white/10 text-white placeholder:text-white/20 py-4 pl-12 pr-4 rounded-xl focus:outline-none focus:border-emerald-500/50 transition-colors"
                                />
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-emerald-100/60">Authorized Clearance Units</label>
                                <div className="relative group">
                                  <Users className="absolute left-4 top-1/2 transform -translate-y-1/2 h-4 w-4 text-emerald-100/40 group-focus-within:text-emerald-400 transition-colors" />
                                  <input
                                    type="number"
                                    placeholder="Number of occupants..."
                                    value={householdData.members}
                                    onChange={(e) => setHouseholdData({...householdData, members: e.target.value})}
                                    className="w-full bg-[#0a0b14] border border-white/10 text-white placeholder:text-white/20 py-4 pl-12 pr-4 rounded-xl focus:outline-none focus:border-emerald-500/50 transition-colors bg-transparent"
                                  />
                                </div>
                              </div>
                              <div className="space-y-2">
                                <label className="text-[10px] font-black uppercase tracking-widest text-emerald-100/60">Structure Type</label>
                                <input
                                  type="text"
                                  placeholder="e.g., Detached, High-rise"
                                  value={householdData.propertyType}
                                  onChange={(e) => setHouseholdData({...householdData, propertyType: e.target.value})}
                                  className="w-full bg-[#0a0b14] border border-white/10 text-white placeholder:text-white/20 py-4 px-4 rounded-xl focus:outline-none focus:border-emerald-500/50 transition-colors"
                                />
                              </div>
                            </div>
                           </div>
                       )}
                    </div>

                    <div className="flex justify-end mt-8 pt-6 border-t border-white/10">
                      <Button 
                        onClick={handleComplete}
                        disabled={!isFormValid() || isLoading}
                        className={`font-black text-[10px] uppercase tracking-widest py-6 px-10 rounded-2xl transition-all shadow-[0_0_20px_rgba(0,0,0,0.5)] ${
                          selectedType === 'organization' ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_20px_rgba(37,99,235,0.4)]' : 'bg-emerald-600 hover:bg-emerald-500 text-white shadow-[0_0_20px_rgba(16,185,129,0.4)]'
                        } disabled:opacity-50 min-w-[200px]`}
                      >
                        {isLoading ? (
                            <span className="flex items-center gap-2">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Synchronizing...
                            </span>
                        ) : (
                            <span className="flex items-center">
                                Finalize Network <ArrowRight className="h-4 w-4 ml-3 flex-shrink-0" />
                            </span>
                        )}
                      </Button>
                    </div>
                 </motion.div>
              )}
            </AnimatePresence>
          </div>
        </GlassPanel>

        <div className="text-center text-[10px] font-bold uppercase tracking-widest text-white/20">
            CONNECTION END-TO-END ENCRYPTED VIA AES-256
        </div>
      </div>
    </div>
  );
}